import React, {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import {chartColors, getHtmlLegendPlugin} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {Chart} from "chart.js";
import {getBackgroundColors, getBackgroundHoverColors} from "../../../../../utils/graphColors.js";
import moment from "moment-timezone";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import Button from "../../../../../components/Button.jsx";

export default function OrdersInTimeGraph(
    {
        project,
        fullWidth = false,
        showLegend = false,
        showHideButton = false,
        hidden = false,
        setHidden = () => {
        },
    }
) {
    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    const canvas = useRef(null);
    const legend = useRef(null);

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';

    const {gridColor, textColor, tooltipBodyColor, tooltipBgColor, tooltipBorderColor} = chartColors;

    let [userSettings] = useUserSettings();

    let {
        since,
        until,
        sourcesRemoved,
        campaignSearchFilter,
        campaignsAllowListed,
        centresAllowListed,
        domainsAllowListed,
        sourceRegister,
        campaignRegister,
    } = useContext(FilterContext);

    let [data, setData] = useState(null);

    let authorityConfiguration = useAuthorityConfiguration();

    useEffect(
        () => {
            new ErpCalculationApi(authorityConfiguration).ordersInTime({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedDomainIds: domainsAllowListed,
                    selectedCentreIds: centresAllowListed,
                },
            }).then(setData).catch(e => et(e).then(toast.error));
        },
        [
            project.id,
            since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'),
            centresAllowListed,
            domainsAllowListed,
        ]
    );

    useEffect(() => {
        const ctx = canvas.current;

        //  Chart.register(LogarithmicScale);

        const pluginsArray = [];

        if (showLegend) {
            pluginsArray.push(getHtmlLegendPlugin(legend));
        }

        // eslint-disable-next-line no-unused-vars
        // noinspection JSUnresolvedReference
        const newChart = new Chart(ctx, {
            type: 'line',
            data: {
                datasets: Object.values(data?.dataSources ?? []).map((campaign, index) => {
                    sourceRegister(campaign.integrationName);
                    campaignRegister(campaign);

                    if (sourcesRemoved.includes(campaign.integrationName)) {
                        return {};
                    }

                    // noinspection JSUnresolvedReference
                    if (
                        campaignSearchFilter.length > 0 &&
                        !campaign.campaignName
                            .toLowerCase()
                            .includes(campaignSearchFilter.toLowerCase())
                    ) {
                        return {};
                    }

                    if (
                        campaignsAllowListed.length > 0 &&
                        !campaignsAllowListed.includes(campaign.remoteId)
                    ) {
                        return {};
                    }

                    return {
                        label: campaign.campaignName,
                        data: userSettings?.isMarketing ? campaign.dataGross : campaign.dataNet,
                        fill: false,
                        backgroundColor: getBackgroundHoverColors()[index],
                        borderColor: getBackgroundColors()[index],
                        borderWidth: 2,
                        pointBorderWidth: 1,
                    };
                }),
            },
            options: {
                animations: null,
                responsive: true,
                scales: {
                    y: {
                        border: {
                            display: false,
                        },
                        ticks: {
                            maxTicksLimit: 7,
                            color: darkMode ? textColor.dark : textColor.light,
                        },
                        grid: {
                            color: darkMode ? gridColor.dark : gridColor.light,
                        },
                    },
                    x: {
                        border: {
                            display: false,
                        },
                        ticks: {
                            padding: 10,
                            align: 'center',
                            color: darkMode ? textColor.dark : textColor.light,
                            callback: function (val, index) {
                                return moment(data?.labels[index] ?? '').format('DD.MM.');
                            },
                        },
                        grid: {
                            display: false,
                        },
                    },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => false, // Disable tooltip title
                            label: (context) => {
                                return [
                                    moment(context.label).format('DD.MM.'),
                                    context.dataset.label,
                                    t('orders.x-orders', {
                                        count: Object.values(context.dataset.data)[
                                            context.dataIndex
                                            ],
                                    }),
                                ];
                            },
                        },
                        bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                        backgroundColor: darkMode ? tooltipBgColor.dark : tooltipBgColor.light,
                        borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                        opacity: 0.8,
                    },
                    legend: { display: false, },
                    ...(showLegend && {
                        htmlLegend: {
                            containerID: 'lgnd-orders-intime',
                        },
                    }),
                },
                interaction: {
                    intersect: false,
                    mode: 'nearest',
                },
                maintainAspectRatio: false,
                elements: {
                    point: {
                        radius: 2,
                        hoverRadius: 3,
                    },
                },
            },
            plugins: pluginsArray,
        });

        return () => {
            newChart.destroy();
        };
    }, [data, userSettings, sourcesRemoved, campaignsAllowListed, campaignSearchFilter, showLegend]);

    return (<LoadingComponent
        loading={data === null}
        className={
            'comp__roundedBox bg-white dark:bg-slate-800' + ' ' +
            'border-slate-200 dark:border-slate-700' + ' ' +
            'text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300' + ' ' +
            'shadow-xl rounded-md border-1 ' +
            (fullWidth ? 'col-span-12' : 'col-span-12 md:col-span-6') + ' ' +
            (!showLegend ? 'pb-2' : '')
        }>
        <div
            className={'flex justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
            <h2 className={'text-lg font-semibold text-gray-800 dark:text-gray-100'}>
                {t('project.orders-in-time')}
            </h2>

            {showHideButton &&
                <Button
                    variant={'outline-violet'}
                    onClick={() => setHidden(!hidden)}
                >
                    {hidden ? t('show') : t('hide')}
                </Button>
            }
        </div>
        <div className={'pl-4 pt-3 ' + (hidden ? 'hidden' : '')}>
            <div>
                <canvas ref={canvas} height={showLegend ? 450 : 250}></canvas>
            </div>
            <div ref={legend} className="legend-wrap"></div>
        </div>
    </LoadingComponent>);
}
